import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/context/LanguageContext";

const FIELD = "w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/40 focus:border-gold focus:outline-none transition-colors";

export function OwnerForm({ compact = false }) {
  const t = useT().ownerForm;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", location: "", property_type: "", bedrooms: "", already_listed: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await api.post("/leads", form); setSent(true); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <motion.div data-testid="form-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-ink/15 p-10 sm:p-14 text-center">
      <p className="overline text-gold mb-6">{t.successOverline}</p>
      <p className="font-serif text-3xl sm:text-4xl leading-snug max-w-xl mx-auto">{t.success}</p>
    </motion.div>
  );

  return (
    <form onSubmit={submit} data-testid="owner-form" className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
        <input value={form.location} onChange={upd("location")} placeholder={t.location} className={FIELD} data-testid="input-location" />
        <select value={form.property_type} onChange={upd("property_type")} className={FIELD} data-testid="input-property-type">
          <option value="">{t.propertyType}</option>
          {t.types.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
        </select>
        <input value={form.bedrooms} onChange={upd("bedrooms")} placeholder={t.bedrooms} className={FIELD} data-testid="input-bedrooms" />
        <select value={form.already_listed} onChange={upd("already_listed")} className={FIELD} data-testid="input-already-listed">
          <option value="">{t.alreadyListed}</option>
          <option value={t.yes}>{t.yes}</option>
          <option value={t.no}>{t.no}</option>
        </select>
        <input required value={form.first_name} onChange={upd("first_name")} placeholder={t.firstName} className={FIELD} data-testid="input-first-name" />
        <input required value={form.last_name} onChange={upd("last_name")} placeholder={t.lastName} className={FIELD} data-testid="input-last-name" />
        <input required type="email" value={form.email} onChange={upd("email")} placeholder={t.email} className={FIELD} data-testid="input-email" />
        <input value={form.phone} onChange={upd("phone")} placeholder={t.phone} className={FIELD} data-testid="input-phone" />
      </div>
      {error && <p className="text-destructive text-sm" data-testid="form-error">{error}</p>}
      <button type="submit" disabled={loading} data-testid="submit-owner-form" className="bg-ink text-ivory px-10 py-4 overline hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
        {loading ? t.sending : t.submit}
      </button>
      <p className="text-xs text-ink/45 leading-relaxed max-w-2xl" data-testid="form-rgpd">
        {t.rgpd.split("{link}")[0]}
        <a href="/confidentialite" className="underline underline-offset-2 hover:text-gold transition-colors">{t.rgpdLink}</a>
        {t.rgpd.split("{link}")[1] || ""}
      </p>
    </form>
  );
}
